import React, { createContext, useContext, useState, ReactNode } from 'react';

import {
    BaseModel,
    LoadingSession,
    PackagingFlow,
    PackagingFlowDirection,
    PackagingVoucher,
    Store,
} from '../queries/api/types';

interface PackagingFlowContextProps {
    selectedStore: Store | null;
    setSelectedStore: (name: Store | null) => void;
    loadingSession: LoadingSession | null;
    setLoadingSession: (data: LoadingSession | null) => void;
    voucher: string;
    setVoucher: (voucher: string) => void;
    isAfterLoading: boolean;
    setIsAfterLoading: (isAfterLoading: boolean) => void;
    packageFlow: PackagingFlow | null;
    setPackageFlow: React.Dispatch<React.SetStateAction<PackagingFlow | null>>;
    packageType: BaseModel | null;
    packageCategory: BaseModel | null;
    setPackageType: (id: BaseModel | null) => void;
    setPackageCategory: (id: BaseModel | null) => void;
    packageFlowId: string | null;
    setPackageFlowId: (id: string | null) => void;
    setFlowType: (type: number) => void;
    flowType: number;
    recipientLabel: string;
    setRecipientLabel: (label: string) => void;
    transporterId: string | null;
    setTransporterId: (id: string | null) => void;
    transporterName: string;
    setTransporterName: (label: string) => void;
    organizationId: string;
    setOrganizationId: (id: string) => void;
    cmr: string;
    setCmr: (cmr: string) => void;
    supplierId: string;
    setSupplierId: (id: string) => void;
    warehouse: BaseModel | null;
    setWarehouse: (warehouse: BaseModel | null) => void;
    supplierName: string;
    setSupplierName: (id: string) => void;
    reset: () => void;
    movementType: any;
    setMovementType: (value: any) => void;
    selectedProvider: BaseModel | undefined;
    setSelectedProvider: (value: BaseModel) => void;
    selectedParc: BaseModel | null;
    setSelectedParc: (value: BaseModel | null) => void;
    providers: any[];
    setProviders: (value: BaseModel[]) => void;
    parcPalettes: any[];
    setParcPalettes: (value: BaseModel[]) => void;
    isUpdatePackage: boolean;
    setIsUpdatePackage: (isUpdatePackage: boolean) => void;
    packagingFlowData: PackagingFlow[];
    setPackagingFlowData: (data: PackagingFlow[]) => void;
    originalQuantities: { [key: string]: number };
    setOriginalQuantities: (id: string, count: number) => void;
    updateComment: string;
    setUpdateComment: (isUpdateComment: string) => void;
    vouchers: PackagingVoucher[];
    setVouchers: React.Dispatch<React.SetStateAction<PackagingVoucher[]>>;
    selectedSender: BaseModel | undefined;
    setSelectedSender: (value: BaseModel) => void;
    selectedReciever: BaseModel | undefined;
    setSelectedReciever: (value: BaseModel) => void;
    isModifierVisible: boolean;
    setIsModifierVisible: (value: boolean) => void;
    packageToEdit: any;
    setPackageToEdit: (value: any) => void;
    isPackageToEdit: boolean;
    setIsPackageToEdit: (value: boolean) => void;
    isModifierClicked: boolean;
    setIsModifierClicked: (value: boolean) => void;
    uploadedFiles: Array<{ file: File; type: string }>;
    setUploadedFiles: React.Dispatch<React.SetStateAction<Array<{ file: File; type: string }>>>;
    selectedStoreMovement: BaseModel | undefined;
    selectedSupplier: BaseModel | undefined;
    setSelectedStoreMovement: (value: BaseModel) => void;
    setSelectedSupplier: (value: BaseModel) => void;
    modifiedCounterName: string;
    setModifiedCounterName: (value: string) => void;
}
const PackagingFlowContext = createContext<PackagingFlowContextProps | undefined>(undefined);

export const PackagingFlowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [loadingSession, setLoadingSession] = useState<LoadingSession | null>(null);
    const [voucher, setVoucher] = useState<string>('');
    const [cmr, setCmr] = useState<string>('');
    const [isAfterLoading, setIsAfterLoading] = useState<boolean>(false);
    const [packageFlow, setPackageFlow] = useState<PackagingFlow | null>(null);
    const [packageType, setPackageType] = useState<BaseModel | null>(null);
    const [packageCategory, setPackageCategory] = useState<BaseModel | null>(null);
    const [packageFlowId, setPackageFlowId] = useState<string | null>(null);
    const [flowType, setFlowType] = useState<number>(PackagingFlowDirection.ShippedToStore);
    const [recipientLabel, setRecipientLabel] = useState<string>('');
    const [transporterId, setTransporterId] = useState<string | null>(null);
    const [organizationId, setOrganizationId] = useState<string>('');
    const [supplierId, setSupplierId] = useState<string>('');
    const [warehouse, setWarehouse] = useState<BaseModel | null>(null);
    const [movementType, setMovementType] = useState<any>(null);
    const [selectedProvider, setSelectedProvider] = useState<BaseModel | undefined>(undefined);
    const [selectedParc, setSelectedParc] = useState<BaseModel | null>(null);
    const [providers, setProviders] = useState<BaseModel[]>([]);
    const [parcPalettes, setParcPalettes] = useState<BaseModel[]>([]);
    const [supplierName, setSupplierName] = useState<string>('');
    const [transporterName, setTransporterName] = useState<string>('');
    const [isUpdatePackage, setIsUpdatePackage] = useState<boolean>(false);
    const [packagingFlowData, setPackagingFlowData] = useState<PackagingFlow[]>([]);
    const [originalQuantities, setOriginalQuantitiesState] = useState<{ [key: string]: number }>({});
    const [selectedSender, setSelectedSender] = useState<BaseModel | undefined>(undefined);
    const [selectedReciever, setSelectedReciever] = useState<BaseModel | undefined>(undefined);
    const [isModifierVisible, setIsModifierVisible] = useState<boolean>(true);
    const [packageToEdit, setPackageToEdit] = useState<any>(true);
    const [isPackageToEdit, setIsPackageToEdit] = useState<boolean>(false);
    const [isModifierClicked, setIsModifierClicked] = useState<boolean>(false);
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ file: File; type: string }>>([]);
    const setOriginalQuantities = (packageId: string, count: number) => {
        setOriginalQuantitiesState((prev) => ({ ...prev, [packageId]: count }));
    };
    const [updateComment, setUpdateComment] = useState<string>('');
    const [vouchers, setVouchers] = useState<PackagingVoucher[]>([]);
    const [selectedStoreMovement, setSelectedStoreMovement] = useState<BaseModel | undefined>(undefined);
    const [selectedSupplier, setSelectedSupplier] = useState<BaseModel | undefined>(undefined);
    const [modifiedCounterName, setModifiedCounterName] = useState<string>('');

    const reset = () => {
        setLoadingSession(null);
        setVoucher('');
        setCmr('');
        setIsAfterLoading(false);
        setPackageFlow(null);
        setPackageType(null);
        setPackageCategory(null);
        setPackageFlowId(null);
        setFlowType(PackagingFlowDirection.ShippedToStore);
        setRecipientLabel('');
        setTransporterId('');
        setSupplierId('');
        setWarehouse(null);
        setSupplierName('');
        setTransporterName('');
        setParcPalettes([]);
        setProviders([]);
        setSelectedProvider(undefined);
        setSelectedParc(null);
        setMovementType(null);
        setOriginalQuantitiesState({});
        setPackagingFlowData([]);
        setUpdateComment('');
        setVouchers([]);
        setSelectedSender(undefined);
        setSelectedReciever(undefined);
        setIsModifierVisible(true);
        setPackageToEdit(null);
        setIsPackageToEdit(false);
        setIsModifierClicked(false);
        setUploadedFiles([]);
        setSelectedStoreMovement(undefined);
        setSelectedSupplier(undefined);
        setModifiedCounterName('');
    };

    return (
        <PackagingFlowContext.Provider
            value={{
                selectedStore,
                setSelectedStore,
                loadingSession,
                setLoadingSession,
                voucher,
                setVoucher,
                isAfterLoading,
                setIsAfterLoading,
                packageFlow,
                setPackageFlow,
                packageType,
                packageCategory,
                setPackageType,
                setPackageCategory,
                packageFlowId,
                setPackageFlowId,
                flowType,
                setFlowType,
                recipientLabel,
                setRecipientLabel,
                transporterId,
                setTransporterId,
                organizationId,
                setOrganizationId,
                cmr,
                setCmr,
                supplierId,
                setSupplierId,
                warehouse,
                setWarehouse,
                supplierName,
                setSupplierName,
                reset,
                movementType,
                setMovementType,
                selectedProvider,
                setSelectedProvider,
                selectedParc,
                setSelectedParc,
                providers,
                setProviders,
                parcPalettes,
                setParcPalettes,
                transporterName,
                setTransporterName,
                isUpdatePackage,
                setIsUpdatePackage,
                packagingFlowData,
                setPackagingFlowData,
                originalQuantities,
                setOriginalQuantities,
                updateComment,
                setUpdateComment,
                vouchers,
                setVouchers,
                selectedSender,
                setSelectedSender,
                selectedReciever,
                setSelectedReciever,
                isModifierVisible,
                setIsModifierVisible,
                packageToEdit,
                setPackageToEdit,
                isPackageToEdit,
                setIsPackageToEdit,
                isModifierClicked,
                setIsModifierClicked,
                uploadedFiles,
                setUploadedFiles,
                setSelectedStoreMovement,
                selectedStoreMovement,
                setSelectedSupplier,
                selectedSupplier,
                setModifiedCounterName,
                modifiedCounterName,
            }}
        >
            {children}
        </PackagingFlowContext.Provider>
    );
};

export const usePackagingFlow = (): PackagingFlowContextProps => {
    const context = useContext(PackagingFlowContext);
    if (!context) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
};
